import { useCallback, useEffect, useMemo } from 'react';

import useDentalHistoryStore from '../../../../stores/dentalHistoryStore';
import { markerFor } from './markerMap';
import { parseSurfacesFromContent } from './parseSurfacesFromContent';

// Severity ordering used to pick the most-clinically-significant
// diagnosis when a tooth has multiple. severe > moderate > mild.
const SEVERITY_RANK = { severe: 3, moderate: 2, mild: 1 };
function maxSeverity(a, b) {
  if (!a) return b || null;
  if (!b) return a || null;
  return (SEVERITY_RANK[a] || 0) >= (SEVERITY_RANK[b] || 0) ? a : b;
}

/**
 * `usePatientToothHistory(patientId)`.
 *
 * Aggregates a patient's completed tooth-tagged procedures + findings
 * into chart-ready maps. Reads three server-side sources via the
 * shared `dentalHistoryStore`:
 *   1. fetchPatientHistory  — list of visits for the patient
 *   2. listSittingsForVisit — completed/in-flight procedures
 *   3. fetchVisitMetadata    — note + diagnosis NOTE rows
 *
 * Phase 13 — this hook used to fire its OWN fetch chain on every mount.
 * With four+ consumers on a visit page (slab, chart, banner, findings
 * strip, add-procedure picker) that meant duplicate downloads of the
 * same data + uncoordinated re-renders. The fetches + caching now live
 * in `dentalHistoryStore` (request coalescing, realtime invalidation,
 * 60s eviction after the last consumer unmounts); this hook is a thin
 * adapter that:
 *   1. subscribes the consumer to the patient's cache slot (refcount
 *      + eviction-timer reset on unmount),
 *   2. triggers `load()` on mount (no-op if cache is `ready`),
 *   3. computes the chart-ready projections via `buildIndices` and
 *      memoises them by the store's monotonic `version`.
 *
 * Public return shape unchanged from the pre-store hook — every
 * consumer continues to use:
 *   { markers, entriesByToken, notesByToken, surfaceMarkersByTooth,
 *     diagnosesByVisit, notesByVisit, proceduresByVisit, visits,
 *     visitById, loading, error, refresh, addOptimisticEntry }
 */

// ── Token parser ─────────────────────────────────────────────────────
const SURFACE_RE = /^([1-4][1-8]|[5-8][1-5])(?:-([MDBLO]))?$|^(Q[1-4]|FM)$/;

export function parseToothToken(rawToken) {
  if (!rawToken) return null;
  const isGum = rawToken.startsWith('gum:');
  const body = isGum ? rawToken.slice(4) : rawToken;
  const m = SURFACE_RE.exec(body);
  if (!m) return null;
  const tooth = m[1] || m[3];
  const surface = m[2] || null;
  return {
    tooth,
    surface,
    scope: isGum ? 'gum' : 'tooth',
    fdiKey: tooth,
    rawKey: rawToken,
  };
}

const TERMINAL_SITTING_STATES = new Set(['completed', 'paid']);
// `in_progress` is the canonical "doctor is actively working" state —
// split out so the chart can render it with a distinct teal half-fill
// (status priority between 'planned' and 'completed').
const IN_PROGRESS_SITTING_STATES = new Set(['in_progress']);
const PLANNED_SITTING_STATES = new Set([
  'awaiting_payment', 'payment_cleared', 'paid_pending_execution',
  'scheduled', 'held', 'ready', 'pending_approval', 'approved',
]);
// EC4: cancelled / voided sittings are dropped from marker computation
// so the chart doesn't carry stale planned-dots forever. They still
// exist in the timeline (read-only) if a caller wants to audit them.
const IGNORED_SITTING_STATES = new Set([
  'cancelled', 'voided', 'rejected',
]);

export function deriveSittingState(status) {
  if (!status) return null;
  if (IGNORED_SITTING_STATES.has(status)) return null;
  if (TERMINAL_SITTING_STATES.has(status)) return 'completed';
  if (IN_PROGRESS_SITTING_STATES.has(status)) return 'in_progress';
  if (PLANNED_SITTING_STATES.has(status)) return 'planned';
  return null;
}

// Marker kinds that are anatomically PERMANENT — these stay visible in
// 'fresh' mode (cross-visit filter) since their absence would visually
// resurrect treated/extracted teeth.
export const TERMINAL_MARKER_KINDS = new Set([
  'missing', 'crown', 'bridge', 'filled', 'rct', 'fractured', 'impacted', 'implant',
]);

// EC1 / C6.5: the inter-visit threshold beyond which the chart opens in
// "fresh" mode. Tuned to 5 months (~150 days) so 6-month recall cycles
// stay in 'full' view; only true long-gap returns trigger fresh.
export const STALE_SESSION_DAYS = 150;

// Local-day check against the clinic's wall clock. `NurseTaskWidget`'s
// UTC slice is unsafe here — a procedure completed at 23:30 local would
// fall on the "next UTC day" in tz=+ regions.
export function isLocalToday(iso) {
  if (!iso) return false;
  const t = new Date(iso);
  if (Number.isNaN(t.getTime())) return false;
  const n = new Date();
  return t.getFullYear() === n.getFullYear()
    && t.getMonth() === n.getMonth()
    && t.getDate() === n.getDate();
}

// Shared "Recent" predicate. The chart's marker filter and the side
// summary BOTH call this so the two surfaces can never silently
// disagree on which tooth is in-scope for the dentist's current visit.
export function passesRecentFilter(marker, currentVisitId, recentVisitIds = null) {
  if (!marker) return false;
  if (TERMINAL_MARKER_KINDS.has(marker.kind)) return true;
  if ((marker.planned_count || 0) > 0) return true;
  if (marker.last_visit_id === currentVisitId) return true;
  if (recentVisitIds && marker.last_visit_id
      && recentVisitIds.has(marker.last_visit_id)) return true;
  return false;
}

// ── Pure bucket builder ──────────────────────────────────────────────
//
// Single source of truth for the chart's index maps. Deterministic
// given the same inputs — used by reload() (server-truth) AND merged
// with optimistic pending entries on render.
function buildIndices({ sittings = [], metaEntries = [] }) {
  const markers = new Map();
  const entriesByToken = new Map();
  const notesByToken = new Map();
  const surfaceMarkersByTooth = new Map();
  const diagnosesByVisit = new Map();
  const notesByVisit = new Map();
  const proceduresByVisit = new Map();
  // F6.4 — per-diagnosis link aggregation. Keyed by diagnosis meta_id;
  // tracks how many linked sittings are planned vs completed so we can
  // mark a diagnosis as RESOLVED (all linked procedures done, none
  // pending) and promote the chart's status dot to green.
  const diagnosisLinkState = new Map();
  const bumpLink = (metaId, isCompleted) => {
    if (!metaId) return;
    const prev = diagnosisLinkState.get(metaId) || {
      plannedCount: 0, completedCount: 0,
    };
    if (isCompleted) prev.completedCount += 1;
    else prev.plannedCount += 1;
    diagnosisLinkState.set(metaId, prev);
  };

  const ensureSurfaceMap = (fdi) => {
    if (!surfaceMarkersByTooth.has(fdi)) {
      surfaceMarkersByTooth.set(fdi, {
        M: null, D: null, B: null, L: null, O: null,
      });
    }
    return surfaceMarkersByTooth.get(fdi);
  };

  const upsertSurfaceMark = (fdi, surface, mark) => {
    if (!surface) return;
    const map = ensureSurfaceMap(fdi);
    const prev = map[surface] || {};
    map[surface] = {
      kind: mark.kind || prev.kind || null,
      state: mark.state || prev.state || null,
      has_diagnosis: !!(mark.has_diagnosis || prev.has_diagnosis),
      has_note: !!(mark.has_note || prev.has_note),
      has_gum: !!(mark.has_gum || prev.has_gum),
      severity: mark.severity || prev.severity || null,
      summary: mark.summary || prev.summary || null,
    };
  };

  for (const s of sittings) {
    const visitId = s?.visit_id;
    if (visitId) {
      if (!proceduresByVisit.has(visitId)) proceduresByVisit.set(visitId, []);
      proceduresByVisit.get(visitId).push(s);
    }
    const sittingState = deriveSittingState(s?.status);
    const isTerminal = sittingState === 'completed';
    const tokens = s?.tooth_numbers;
    // Cancelled / voided procedures don't colour the tooth (no marker), but the
    // summary surfaces them under the "Cancelled" filter — record them in
    // entriesByToken only, then skip marker building.
    if (IGNORED_SITTING_STATES.has(s?.status)
        && Array.isArray(tokens) && tokens.length > 0) {
      for (const token of tokens) {
        if (!parseToothToken(token)) continue;
        if (!entriesByToken.has(token)) entriesByToken.set(token, []);
        entriesByToken.get(token).push(s);
      }
      continue;
    }
    if (!sittingState || !Array.isArray(tokens) || tokens.length === 0) continue;
    const procedure = s.procedure_snapshot || {
      name: s.procedure_name,
      category: 'dental',
      specialty: s.procedure_specialty,
    };
    const kind = markerFor(procedure);
    const isFinding = (procedure?.specialty || '') === 'finding';
    // F6.4 — record the diagnosis-link resolution state ONCE per
    // sitting (not per token) so multi-tooth procedures don't
    // double-count.
    if (s?.linked_diagnosis_metadata_id) {
      bumpLink(s.linked_diagnosis_metadata_id, isTerminal);
    }
    for (const token of tokens) {
      const parsed = parseToothToken(token);
      if (!parsed) continue;
      const { fdiKey, surface, scope } = parsed;
      const isGum = scope === 'gum';

      const sittingVisitId = s?.visit_id || null;
      const sittingLinkedDiagId = s?.linked_diagnosis_metadata_id || null;
      const sittingAssignee = s?.assigned_to_name || null;
      const sittingNotes = s?.notes || null;

      if (isTerminal) {
        const existing = markers.get(fdiKey);
        // F6.2 — record every completed sitting on the marker's
        // linked_procedures list, even when no diagnosis-link was
        // stored. Lets the chart + summary surface "Composite filling
        // · Dr. Sharma · completed" on the tooth regardless of
        // whether the linkage field made it onto the row at create
        // time (a real problem with sittings created before the
        // Phase 5 backend deploy).
        const completedLink = {
          meta_id: sittingLinkedDiagId,           // may be null
          name: procedure?.name || kind,
          assignee: sittingAssignee,
          status: s?.status || 'completed',
          sitting_id: s?.sitting_id || null,
          completed_at: s?.completed_at || null,
        };
        if (!existing) {
          markers.set(fdiKey, {
            kind,
            count: 1,
            source: isFinding ? 'finding' : 'this-clinic',
            has_gum: isGum || false,
            completed_count: 1,
            planned_count: 0,
            last_visit_id: sittingVisitId,
            linked_procedures: [completedLink],
          });
        } else {
          existing.count += 1;
          existing.completed_count = (existing.completed_count || 0) + 1;
          if (!isFinding) existing.source = 'this-clinic';
          if (!isFinding) existing.kind = kind;
          if (isGum) existing.has_gum = true;
          if (sittingVisitId) existing.last_visit_id = sittingVisitId;
          if (!Array.isArray(existing.linked_procedures)) existing.linked_procedures = [];
          existing.linked_procedures.push(completedLink);
        }
      } else if (sittingState === 'planned' || sittingState === 'in_progress') {
        // in_progress is treated as an active-work variant of planned —
        // same marker fields, but `in_progress_count` is bumped so the
        // half-fill renderer can pick the teal mid-color over the blue
        // start color. Status priority lives in statusColors.js.
        const isInProgress = sittingState === 'in_progress';
        const existing = markers.get(fdiKey);
        // EC1: if a planned sitting matches the kind of an already-
        // completed history on this tooth, flag it as re-treatment so
        // the hover tooltip can call this out.
        const isRetreatment = !!existing
          && existing.completed_count > 0
          && existing.kind === kind;
        if (!existing) {
          markers.set(fdiKey, {
            kind,
            count: 0,
            source: isFinding ? 'finding' : 'this-clinic',
            has_gum: isGum || false,
            planned_count: 1,
            completed_count: 0,
            in_progress_count: isInProgress ? 1 : 0,
            planned_summary: procedure?.name || kind,
            planned_assignee: sittingAssignee,
            planned_status: s?.status || null,
            planned_sitting_id: s?.sitting_id || null,
            planned_linked_diag_id: sittingLinkedDiagId,
            planned_notes: sittingNotes,
            is_retreatment: false,
            last_visit_id: sittingVisitId,
            linked_procedures: [{
              meta_id: sittingLinkedDiagId,
              name: procedure?.name || kind,
              assignee: sittingAssignee,
              status: s?.status || null,
              sitting_id: s?.sitting_id || null,
            }],
          });
        } else {
          existing.planned_count = (existing.planned_count || 0) + 1;
          if (isInProgress) {
            existing.in_progress_count = (existing.in_progress_count || 0) + 1;
          }
          if (!existing.planned_summary) existing.planned_summary = procedure?.name || kind;
          if (!existing.planned_assignee) existing.planned_assignee = sittingAssignee;
          // Promote planned_status to in_progress if any sitting on this
          // tooth is actively underway — that's what the half-fill reads.
          if (!existing.planned_status || isInProgress) {
            existing.planned_status = s?.status || existing.planned_status;
          }
          if (!existing.planned_sitting_id) existing.planned_sitting_id = s?.sitting_id || null;
          if (!existing.planned_linked_diag_id) existing.planned_linked_diag_id = sittingLinkedDiagId;
          if (!existing.planned_notes && sittingNotes) existing.planned_notes = sittingNotes;
          existing.is_retreatment = existing.is_retreatment || isRetreatment;
          if (isGum) existing.has_gum = true;
          if (sittingVisitId) existing.last_visit_id = sittingVisitId;
          if (!Array.isArray(existing.linked_procedures)) existing.linked_procedures = [];
          existing.linked_procedures.push({
            meta_id: sittingLinkedDiagId,
            name: procedure?.name || kind,
            assignee: sittingAssignee,
            status: s?.status || null,
            sitting_id: s?.sitting_id || null,
          });
        }
      }

      upsertSurfaceMark(fdiKey, surface, {
        kind,
        state: sittingState,
        has_gum: isGum,
        summary: procedure?.name || kind,
      });

      if (!entriesByToken.has(token)) entriesByToken.set(token, []);
      entriesByToken.get(token).push(s);
    }
  }

  // De-dupe metadata entries by meta_id so an optimistic insert
  // doesn't double-render once the server reload arrives.
  const seenMetaIds = new Set();
  let kindMissingCount = 0;
  let kindDiagnosisCount = 0;
  for (const entry of metaEntries) {
    if (entry?.meta_type !== 'NOTE') continue;
    const metaId = entry?.meta_id;
    if (metaId) {
      if (seenMetaIds.has(metaId)) continue;
      seenMetaIds.add(metaId);
    }
    const rawKind = entry?.kind != null ? String(entry.kind).toLowerCase() : null;
    const hasSeverity = !!entry?.severity;
    const tokenList = Array.isArray(entry?.tooth_numbers) ? entry.tooth_numbers : [];
    const hasSurfaceSuffix = tokenList.some(
      (t) => typeof t === 'string' && /-[MDBLO]$/.test(t),
    );
    const looksLikeDiagnosis = (
      rawKind === 'diagnosis'
      || (rawKind == null && hasSeverity)
      || (rawKind == null && hasSurfaceSuffix)
    );
    if (rawKind == null) kindMissingCount += 1;
    if (looksLikeDiagnosis) kindDiagnosisCount += 1;
    const entryKind = looksLikeDiagnosis ? 'diagnosis' : (rawKind || 'note');
    const visitId = entry?.visit_id;
    if (visitId) {
      if (entryKind === 'diagnosis') {
        if (!diagnosesByVisit.has(visitId)) diagnosesByVisit.set(visitId, []);
        diagnosesByVisit.get(visitId).push(entry);
      } else {
        if (!notesByVisit.has(visitId)) notesByVisit.set(visitId, []);
        notesByVisit.get(visitId).push(entry);
      }
    }
    if (!Array.isArray(tokenList) || tokenList.length === 0) continue;

    // Pull MVP-V1 surface prefix off the diagnosis content (e.g. "[M,O] caries")
    // so the chart can render per-surface dots on the M / O zones even though
    // the backend doesn't yet persist surfaces as a first-class field.
    const parsedSurfaces = entryKind === 'diagnosis'
      ? parseSurfacesFromContent(entry?.content || '')
      : { surfaces: [], rest: entry?.content || '' };
    const contentBody = parsedSurfaces.rest || entry?.content || '';

    for (const token of tokenList) {
      const parsed = parseToothToken(token);
      if (!parsed) continue;
      const { fdiKey, surface, scope } = parsed;
      const isGum = scope === 'gum';
      if (!notesByToken.has(token)) notesByToken.set(token, []);
      notesByToken.get(token).push(entry);

      const entryVisitId = entry?.visit_id || null;
      const entryDiagMetaId = entry?.meta_id || null;
      const existing = markers.get(fdiKey);
      if (!existing) {
        markers.set(fdiKey, {
          kind: isGum ? 'gum' : (entryKind === 'diagnosis' ? 'diagnosis' : 'note'),
          count: 1,
          source: isGum ? 'gum' : (entryKind === 'diagnosis' ? 'diagnosis' : 'note'),
          has_note: !isGum && entryKind !== 'diagnosis',
          has_gum: isGum,
          has_diagnosis: entryKind === 'diagnosis',
          severity: entryKind === 'diagnosis' ? (entry.severity || null) : null,
          surfaces: entryKind === 'diagnosis' ? [...parsedSurfaces.surfaces] : [],
          summary: entryKind === 'diagnosis' ? contentBody : null,
          diagnosis_meta_ids: entryKind === 'diagnosis' && entryDiagMetaId ? [entryDiagMetaId] : [],
          planned_count: 0,
          completed_count: 0,
          last_visit_id: entryVisitId,
        });
      } else {
        if (isGum) existing.has_gum = true;
        else if (entryKind === 'diagnosis') {
          existing.has_diagnosis = true;
          existing.severity = maxSeverity(existing.severity, entry.severity);
          // Union the surfaces across multiple diagnoses on the same tooth.
          const seen = new Set(existing.surfaces || []);
          for (const s of parsedSurfaces.surfaces) seen.add(s);
          existing.surfaces = Array.from(seen);
          if (!existing.summary && contentBody) existing.summary = contentBody;
          if (entryDiagMetaId) {
            if (!Array.isArray(existing.diagnosis_meta_ids)) existing.diagnosis_meta_ids = [];
            if (!existing.diagnosis_meta_ids.includes(entryDiagMetaId)) {
              existing.diagnosis_meta_ids.push(entryDiagMetaId);
            }
          }
        } else existing.has_note = true;
        if (entryVisitId) existing.last_visit_id = entryVisitId;
      }

      upsertSurfaceMark(fdiKey, surface, {
        state: 'existing',
        has_diagnosis: entryKind === 'diagnosis',
        has_note: entryKind !== 'diagnosis' && !isGum,
        has_gum: isGum,
        severity: entry.severity || null,
        summary: entry.content || null,
      });
    }
  }

  // F6.4 — stamp `has_resolved_diagnosis` on each marker. Two paths:
  //   1. Explicit linkage: at least one diagnosis_meta_id has linked
  //      sittings that are all completed (completedCount > 0 AND
  //      plannedCount === 0).
  //   2. Heuristic fallback: the tooth has a diagnosis AND a completed
  //      this-clinic procedure AND no open planned work. This covers
  //      legacy sittings created BEFORE the Phase 5 backend deploy
  //      where linked_diagnosis_metadata_id wasn't persisted, but the
  //      clinical reality is still "diagnosis resolved by procedure".
  for (const marker of markers.values()) {
    const metaIds = marker.diagnosis_meta_ids;
    let anyResolved = false;
    if (Array.isArray(metaIds) && metaIds.length > 0) {
      for (const id of metaIds) {
        const ls = diagnosisLinkState.get(id);
        if (ls && ls.completedCount > 0 && ls.plannedCount === 0) {
          anyResolved = true;
          break;
        }
      }
    }
    // Heuristic fallback: same-tooth diagnosis + completed procedure.
    if (!anyResolved
      && marker.has_diagnosis
      && (marker.completed_count || 0) > 0
      && (marker.planned_count || 0) === 0
      && marker.source !== 'finding') {
      anyResolved = true;
    }
    if (anyResolved) marker.has_resolved_diagnosis = true;
  }

  // F7.3 — dev-only marker dump. Lets the user open devtools and see
  // exactly what the chart thinks about each tooth. Helpful for
  // diagnosing "completed RCT not showing as green" reports — if the
  // log shows completed_count=0 or has_resolved_diagnosis=false, the
  // bug is in the backend response or the projection, not in the
  // chart's rendering layer.
  if (process.env.NODE_ENV === 'development' && markers.size > 0) {
    for (const [fdi, m] of markers.entries()) {
      if (m.completed_count || m.planned_count || m.has_diagnosis) {
        // eslint-disable-next-line no-console
        console.debug('[tooth-chart] marker', fdi, {
          kind: m.kind,
          has_diagnosis: !!m.has_diagnosis,
          has_resolved_diagnosis: !!m.has_resolved_diagnosis,
          planned_count: m.planned_count || 0,
          completed_count: m.completed_count || 0,
          source: m.source,
          severity: m.severity || null,
          linked_procedures: (m.linked_procedures || []).map((p) => ({
            name: p.name, status: p.status, sitting_id: p.sitting_id,
            meta_id: p.meta_id,
          })),
        });
      }
    }
  }

  return {
    markers, entriesByToken, notesByToken, surfaceMarkersByTooth,
    diagnosesByVisit, notesByVisit, proceduresByVisit,
    diagnosisLinkState,
    _kindMissingCount: kindMissingCount,
    _kindDiagnosisCount: kindDiagnosisCount,
  };
}


// Empty patient slot — returned when the caller passes `null` /
// `undefined` patientId. Keeps the hook contract referentially stable
// so consumers don't crash on early renders.
const EMPTY_SLOT = {
  status: 'idle',
  visits: [],
  sittings: [],
  metaEntries: [],
  pending: [],
  pendingSittings: [],
  version: 0,
  error: null,
};


/**
 * Merge optimistic pendingSittings into the server's sittings array
 * for buildIndices.
 *
 *   - **New** sitting (sitting_id not present on the server yet) →
 *     appended.
 *   - **Status flip** on an existing sitting (matching sitting_id) →
 *     fields from the pending row are shallow-merged ON TOP of the
 *     server row. This is how Start (status: 'in_progress') and
 *     Complete (status: 'completed') paint instantly even though the
 *     server hasn't acknowledged yet — the next bundle refetch drops
 *     the pending entry.
 *
 * Keeps the original ordering of server sittings so chart projections
 * stay stable; pending-only sittings tail the array.
 */
function mergeSittingsWithPending(serverSittings, pendingSittings) {
  if (!pendingSittings || pendingSittings.length === 0) {
    return serverSittings || [];
  }
  const pendingById = new Map();
  for (const p of pendingSittings) {
    if (p?.sitting_id) pendingById.set(p.sitting_id, p);
  }
  const merged = [];
  const consumed = new Set();
  for (const s of (serverSittings || [])) {
    const sid = s?.sitting_id;
    if (sid && pendingById.has(sid)) {
      merged.push({ ...s, ...pendingById.get(sid) });
      consumed.add(sid);
    } else {
      merged.push(s);
    }
  }
  // Append pending-only sittings (sitting_id not yet on server).
  for (const p of pendingSittings) {
    if (p?.sitting_id && !consumed.has(p.sitting_id)) merged.push(p);
  }
  return merged;
}


/**
 * C6.1 — detect the inter-visit gap and whether to default the chart to
 * "fresh" mode. Active planned work overrides the freshness reset: an
 * open RCT planned 6 months ago is still active treatment and shouldn't
 * be hidden behind a "Show history" toggle.
 */
function computeFreshnessProjection(visits, markers, currentVisitId) {
  const list = Array.isArray(visits) ? visits : [];
  const cutoff = Date.now() - STALE_SESSION_DAYS * 24 * 60 * 60 * 1000;
  const recentVisitIds = new Set();
  let lastPriorAt = 0;
  for (const v of list) {
    if (!v) continue;
    const t = v.created_at ? new Date(v.created_at).getTime() : 0;
    if (v.visit_id && Number.isFinite(t) && t >= cutoff) {
      recentVisitIds.add(v.visit_id);
    }
    if (v.visit_id === currentVisitId) continue;
    if (Number.isFinite(t) && t > lastPriorAt) lastPriorAt = t;
  }
  const lastPriorVisitAt = lastPriorAt || null;
  const gapDays = lastPriorVisitAt
    ? Math.floor((Date.now() - lastPriorVisitAt) / (1000 * 60 * 60 * 24))
    : null;
  let hasOpenPlannedWork = false;
  for (const m of markers.values()) {
    if ((m.planned_count || 0) > 0) { hasOpenPlannedWork = true; break; }
  }
  const isFreshSession = gapDays != null
    && gapDays > STALE_SESSION_DAYS
    && !hasOpenPlannedWork;
  return {
    lastPriorVisitAt, gapDays, isFreshSession, hasOpenPlannedWork,
    recentVisitIds,
  };
}


// ── Hook (thin adapter over `dentalHistoryStore`) ────────────────────
export default function usePatientToothHistory(patientId, currentVisitId = null) {
  // Pull just this patient's slot. Other patients' state changes don't
  // re-render this consumer because Zustand selectors are referential.
  const slot = useDentalHistoryStore(
    (s) => s.byPatient[patientId] || EMPTY_SLOT,
  );
  const load = useDentalHistoryStore((s) => s.load);
  const refresh = useDentalHistoryStore((s) => s.refresh);
  const addOptimistic = useDentalHistoryStore((s) => s.addOptimistic);
  const addOptimisticSitting = useDentalHistoryStore((s) => s.addOptimisticSitting);
  const subscribe = useDentalHistoryStore((s) => s.subscribe);

  // Subscribe + load on mount; arm the eviction timer on unmount.
  // Re-subscribes cleanly when patientId changes (different patient).
  useEffect(() => {
    if (!patientId) return undefined;
    const unsub = subscribe(patientId);
    load(patientId);
    return () => { unsub(); };
  }, [patientId, subscribe, load]);

  // Merge optimistic-pending into the server's metaEntries + sittings
  // before projecting. The projection itself is memoised on `slot.version`
  // — the store bumps version whenever sittings / metaEntries / pending
  // change for this patient, so other patients' updates don't cause
  // re-computation here.
  const indices = useMemo(() => {
    const allMetaEntries = [
      ...(slot.metaEntries || []),
      ...(slot.pending || []),
    ];
    const allSittings = mergeSittingsWithPending(
      slot.sittings || [],
      slot.pendingSittings || [],
    );
    return buildIndices({
      sittings: allSittings,
      metaEntries: allMetaEntries,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    slot.version, slot.sittings, slot.metaEntries,
    slot.pending, slot.pendingSittings,
  ]);

  if (indices._kindMissingCount > 0 && indices._kindDiagnosisCount === 0) {
    // eslint-disable-next-line no-console
    console.warn(
      '[ToothChart] %d NOTE rows are missing `kind`. Restart backend to surface diagnoses by canonical signal.',
      indices._kindMissingCount,
    );
  }

  // Visit-id → row lookup for callers that decorate sittings / notes
  // with their source-visit date without an extra fetch.
  const visitById = useMemo(() => {
    const map = new Map();
    for (const v of (slot.visits || [])) {
      if (v?.visit_id) map.set(v.visit_id, v);
    }
    return map;
  }, [slot.visits]);

  // Stable identity wrappers so consumers that pass these into
  // useEffect dep arrays don't fire on every render.
  const boundRefresh = useCallback(
    () => (patientId ? refresh(patientId) : Promise.resolve()),
    [patientId, refresh],
  );
  const boundAddOptimistic = useCallback(
    (entry) => (patientId ? addOptimistic(patientId, entry) : undefined),
    [patientId, addOptimistic],
  );
  const boundAddOptimisticSitting = useCallback(
    (sitting) => (patientId ? addOptimisticSitting(patientId, sitting) : undefined),
    [patientId, addOptimisticSitting],
  );

  // C6.1 — freshness projection. Memo so the inter-visit gap math
  // doesn't recompute on every render of the consumer.
  const freshness = useMemo(
    () => computeFreshnessProjection(slot.visits || [], indices.markers, currentVisitId),
    [slot.visits, indices.markers, currentVisitId],
  );

  return {
    markers: indices.markers,
    entriesByToken: indices.entriesByToken,
    notesByToken: indices.notesByToken,
    surfaceMarkersByTooth: indices.surfaceMarkersByTooth,
    diagnosesByVisit: indices.diagnosesByVisit,
    notesByVisit: indices.notesByVisit,
    proceduresByVisit: indices.proceduresByVisit,
    visits: slot.visits || [],
    visitById,
    loading: slot.status === 'loading',
    error: slot.error,
    refresh: boundRefresh,
    addOptimisticEntry: boundAddOptimistic,
    // H4 — exposed so the row-action toolbar can flip a sitting's
    // status locally before the server refresh comes back.
    addOptimisticSitting: boundAddOptimisticSitting,
    // C6 freshness projection — caller decides default mode.
    lastPriorVisitAt: freshness.lastPriorVisitAt,
    gapDays: freshness.gapDays,
    isFreshSession: freshness.isFreshSession,
    hasOpenPlannedWork: freshness.hasOpenPlannedWork,
    recentVisitIds: freshness.recentVisitIds,
    staleSessionDays: STALE_SESSION_DAYS,
  };
}
